/* eslint-disable no-case-declarations */
import BaseModel from "nexia_mobile/models/base_model";
import MediaType from "nexia_mobile/framework/media_type";
import followLink from "nexia_mobile/models/followLink";
import Setting from "nexia_mobile/models/setting";
import Editable from "nexia_mobile/models/mixins/editable";
import Automatable from "nexia_mobile/models/mixins/automatable";
import HasIcon from "nexia_mobile/models/mixins/has_icon";
import * as DeviceFeatures from "nexia_mobile/models/mixins/device_features";
import Url from "nexia_mobile/url";

class Device extends BaseModel {
  validations() {
    return {
      name: {
        required: true,
        label: "Name",
      },
    };
  }

  nestedCollections() {
    return {
      settings: {
        model: Setting,
      },
    };
  }

  onParse(rawAttrs) {
    let attrs = super.onParse(rawAttrs);
    if (attrs.features == null) {
      attrs.features = [];
    }

    this._adaptAttributesToFeatures(attrs);

    this._deviceFeatures = new Set();
    let featureAttrs = {};
    _.each(attrs.features, (feature, name) => {
      let className = _.str
        .classify(feature.name || name)
        .replace(/Xr724/, "XR724");
      // eslint-disable-next-line import/namespace
      let featureMixin = DeviceFeatures[className];
      if (featureMixin == null) {
        return;
      }
      // mixins should return any attributes that represent the state of the device,
      // so that appropriate change events are triggered by the model as the state changes.
      let result = this.addFeature(featureMixin, feature);

      if (_.isObject(result) && !_.isFunction(result)) {
        _.extend(featureAttrs, result);
      }
    });

    return _.extend(featureAttrs, attrs);
  }

  addFeature(featureMixin, featureAttrs = {}) {
    if (this._deviceFeatures == null) {
      this._deviceFeatures = new Set();
    }
    this._deviceFeatures.add(featureMixin);
    if (featureAttrs.actions == null) {
      featureAttrs.actions = {};
    }
    // mixins are defined as methods. call the mixin using this device as
    // the value for "this", so the mixin can add instance methods if it wants to.
    return featureMixin.call(this, featureAttrs);
  }

  hasFeature(featureMixin) {
    if (this._deviceFeatures == null) {
      this._deviceFeatures = new Set();
    }
    return Boolean(this._deviceFeatures.has(featureMixin));
  }

  isNameEditable() {
    let editable = this.get("name_editable");
    return editable == null ? true : editable;
  }

  parseLinksFilterEvents(links) {
    let link = _.first(links);
    if (link) {
      this.set("filterEventsUrl", link.getHref());
    }
  }

  parseLinksFilterVideos(links) {
    let link = _.first(links);
    if (link) {
      this.set("filterVideosUrl", link.getHref());
    }
  }

  parseLinksImage(links) {
    const link = _.first(links);
    if (link) {
      return {
        imageSrc: link.href,
      };
    } else {
      return {
        imageSrc: null,
      };
    }
  }

  hasSettings() {
    return this.get("settings").length > 0;
  }

  toJSON() {
    return { name: this.get("name") };
  }

  getName() {
    return this.get("name");
  }

  getDeviceTypeString() {
    return this.get("type") || "Device";
  }

  executeDeviceAction(link, data, passedOptions = {}) {
    const options = Object.assign(
      {
        method: "POST",
        data,
        fetchModelWhenDone: false,
        updateModelWithResponse: !passedOptions.fetchModelWhenDone,
      },
      passedOptions
    );
    return followLink(this, link, options);
  }

  executeDeviceActionThenFetch(link, data, passedOptions = {}) {
    const options = Object.assign(
      {
        fetchModelWhenDone: true,
      },
      passedOptions
    );
    return this.executeDeviceAction(link, data, options);
  }

  // ---- TODO: change server responses so this isn't necessary.
  // Rearrange the device JSON so it looks like the server is listing all
  // device capabilities in the 'features' array (only some of them are
  // at the moment)
  /* eslint-disable camelcase */
  _adaptAttributesToFeatures(attrs) {
    switch (attrs.type) {
      case "barrier": {
        let feature = {
          name: "barrier",
          status: attrs.current_state_text,
          actions: {},
        };

        if (attrs.operable) {
          feature.actions.operate = {
            href: Url.withAdditionalPath(this.url(), "operate"),
            title: attrs.current_action,
          };
        }

        attrs.features.push(feature);
        break;
      }
      case "camera": {
        attrs.features.push({
          name: "camera",
          connected: attrs.connected,
          quality: attrs.quality,
        });
        break;
      }
      case "dimmer": {
        attrs.features.push({
          name: "dimmer",
          level: attrs.value,
          actions: {
            set_level: {
              href: Url.withAdditionalPath(this.url(), "set"),
            },
          },
        });
        break;
      }
      case "group": {
        let members;
        if (attrs._links != null) {
          members = attrs._links.child;
        }
        if (members != null && !_.isArray(members)) {
          members = [members];
        }

        attrs.features.push({
          name: "group",
          members: members.map((member) => member.data),
        });
        break;
      }
      case "lock": {
        if (attrs.supports_user_codes) {
          const userCodeFeature = {
            name: "access_code",
            has_temporary: attrs.has_temporary_code,
            code_length: attrs.user_code_length,
            resource: {},
            actions: {
              set_temporary: {
                href: Url.withAdditionalPath(this.url(), "set_temporary_code"),
              },
              remove_temporary: {
                href: Url.withAdditionalPath(
                  this.url(),
                  "remove_temporary_code"
                ),
              },
            },
          };

          let collection;
          if (attrs._links != null) {
            collection = attrs._links.child;
          }
          if (
            collection.type === MediaType.COLLECTION &&
            collection.data &&
            collection.data.item_type === MediaType.USER_CODE
          ) {
            userCodeFeature.resource.href = collection.href;
          }

          attrs.features.push(userCodeFeature);
        }
        break;
      }
      case "sensor": {
        const feature = {
          name: "sensor",
          sensor_type: attrs.sensor_type,
        };
        if (attrs.value != null) {
          feature.triggered = attrs.value !== 0;
        }
        if (attrs.current_state_text != null) {
          feature.status = attrs.current_state_text;
        }
        attrs.features.push(feature);

        if (attrs.temperature != null) {
          attrs.features.push({
            name: "sensor",
            sensor_type: DeviceFeatures.Sensor.Type.TEMPERATURE,
            value: attrs.temperature,
            unit: "°",
          });
        }
        break;
      }
      case "smoke_detector": {
        const feature = {
          name: "sensor",
          sensor_type: DeviceFeatures.Sensor.Type.SMOKE,
        };
        if (attrs.current_state_text != null) {
          feature.status = attrs.current_state_text;
        }
        attrs.features.push(feature);
        break;
      }
      case "solar_panel": {
        const feature = { name: "solar_panel" };

        _.extend(feature, _.omit(attrs, "features"));
        attrs.features.push(feature);
        break;
      }
      case "siren":
      case "switch": {
        let status;
        let toggleHref;
        if (attrs.value === 0) {
          status = "off";
          toggleHref = Url.withAdditionalPath(this.url(), "on");
        } else {
          status = "on";
          toggleHref = Url.withAdditionalPath(this.url(), "off");
        }

        let featureName = attrs.type === "siren" ? "alarm" : "switch";

        attrs.features.push({
          name: featureName,
          status,
          actions: {
            toggle: { href: toggleHref },
            on: { href: Url.withAdditionalPath(this.url(), "on") },
            off: { href: Url.withAdditionalPath(this.url(), "off") },
          },
        });
        break;
      }
      case "valve": {
        const feature = {
          name: "valve",
          status: attrs.current_state_text,
          actions: {},
        };

        if (attrs.operable) {
          feature.actions.operate = {
            href: Url.withAdditionalPath(this.url(), "operate"),
            title: attrs.current_action,
          };
        }

        attrs.features.push(feature);
        break;
      }
    }

    if (attrs.has_indoor_humidity) {
      attrs.features.push({
        name: "sensor",
        sensor_type: DeviceFeatures.Sensor.Type.RELATIVE_HUMIDITY,
        value: attrs.indoor_humidity,
        unit: "%",
      });
    }

    if (attrs.has_outdoor_temperature) {
      attrs.features.push({
        name: "sensor",
        sensor_type: DeviceFeatures.Sensor.Type.OUTDOOR_TEMPERATURE,
        value: attrs.outdoor_temperature,
        unit: "°",
      });
    }
  }
  /* eslint-enable camelcase */
}

_.extend(Device.prototype, Editable);
_.extend(Device.prototype, Automatable);
_.extend(Device.prototype, HasIcon);

export default Device;



// WEBPACK FOOTER //
// ./src/models/device.js